import React, { useState, useEffect, useCallback } from 'react';
import FilterAutocomplete from '../../components/Common/FilterAutocomplete';
import { getOriginInfo } from '../../utils/originLabel';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { ContentCopy, Close, Undo, SyncAlt } from '@mui/icons-material';
import {
  Payment,
  TrendingUp,
  AccessTime,
  CheckCircle,
  Cancel,
  Refresh,
  FileDownload,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../components/Common/DataTable';
import { paymentsService } from '../../services';
import { sispService } from '../../services/sisp.service';
import { useTranslation } from 'react-i18next';

interface PaymentData {
  id: string;
  reference: string;
  amount: number;
  status: string;
  paymentMethod: string;
  createdAt: string;
  customerName?: string;
  entityName?: string;
  eventName?: string;
  invoiceReference?: string;
  /** Aprovado pela SISP mas a factura não foi paga (cobrado como compra) */
  faturaNaoPaga?: boolean;
}

interface PaymentStats {
  total: number;
  pagos: number;
  pendentes: number;
  falhados: number;
  abandonados: number;
  valorTotal: number;
}

const Payments: React.FC = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('PAID');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [entidadeFilter, setEntidadeFilter] = useState('all');
  const [servicoFilter, setServicoFilter] = useState('all');
  const [eventoFilter, setEventoFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState<{ entidades: string[]; servicos: { codigo: string; label: string }[]; eventos: { id: string; label: string }[] }>({ entidades: [], servicos: [], eventos: [] });
  const [activeTab, setActiveTab] = useState(0);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentData | null>(null);

  const [refundDialog, setRefundDialog] = useState<{ open: boolean; row: PaymentData | null }>({ open: false, row: null });
  const [refundMotivo, setRefundMotivo] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const [reconcileDialog, setReconcileDialog] = useState<{ open: boolean; merchantRef: string }>({ open: false, merchantRef: '' });
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<{ success: boolean; message: string } | null>(null);

  const TAB_STATUS_MAP = ['PAID', 'PENDING', 'FAILED', 'CANCELLED', 'all'];
  const handleTabChange = (_: any, newTab: number) => {
    setActiveTab(newTab);
    setStatusFilter(TAB_STATUS_MAP[newTab]);
    setPage(0);
  };
  const [stats, setStats] = useState<PaymentStats>({
    total: 0,
    pagos: 0,
    pendentes: 0,
    falhados: 0,
    abandonados: 0,
    valorTotal: 0,
  });

  // Carrega stats reais do backend (totais reais, não só da página)
  const fetchStats = useCallback(async () => {
    try {
      const data = await paymentsService.getStats(30);
      if (data?.stats) {
        setStats({
          total: data.stats.total || 0,
          pagos: data.stats.paid || 0,
          pendentes: data.stats.pending || 0,
          falhados: data.stats.failed || 0,
          abandonados: data.stats.abandoned || 0,
          valorTotal: data.stats.totalPaid || 0,
        });
      }
    } catch { /* silent */ }
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(sourceFilter !== 'all' && { source: sourceFilter }),
        ...(entidadeFilter !== 'all' && { entidade: entidadeFilter }),
        ...(servicoFilter !== 'all' && { servico: servicoFilter }),
        ...(eventoFilter !== 'all' && { evento: eventoFilter }),
      };

      const response = await paymentsService.getPayments(filters);

      const mappedPayments: PaymentData[] = response.data.map((payment: any) => ({
        id: payment.id || '',
        reference: payment.reference || '',
        merchantRef: payment.merchantRef || payment.reference || '',
        amount: payment.amount || 0,
        status: payment.status || '',
        paymentMethod: payment.paymentMethod || '',
        createdAt: payment.createdAt || '',
        customerName: payment.customerName,
        customerEmail: payment.customerEmail,
        entityName: payment.entityName,
        eventName: payment.eventName,
        invoiceReference: payment.invoiceReference,
        faturaNaoPaga: !!Number(payment.faturaNaoPaga),
        source: payment.source || 'legacy',
        deviceType: payment.deviceType || '',
        referer: payment.referer || '',
        ipAddress: payment.ipAddress || '',
        tokenRecarga: payment.tokenRecarga || '',
        origin: payment.origin || '',
        sispErrorCode: payment.sispErrorCode || '',
        sispErrorDescription: payment.sispErrorDescription || '',
        sispErrorDetail: payment.sispErrorDetail || '',
        sispAdditionalError: payment.sispAdditionalError || '',
        sispMessageType: payment.sispMessageType || '',
        sispMerchantResp: payment.sispMerchantResp || '',
      }));

      setPayments(mappedPayments);
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      setError(err.message || t('payments.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, sourceFilter, entidadeFilter, servicoFilter, eventoFilter]);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [fetchPayments, fetchStats]);

  // Opções dos filtros de entidade e serviço (carregadas uma vez)
  // Facturas que a SISP cobrou como compra e não pagou — aviso no topo enquanto houver
  const [facturasNaoPagas, setFacturasNaoPagas] = useState<Awaited<ReturnType<typeof paymentsService.getFacturasNaoPagas>>>([]);
  useEffect(() => {
    paymentsService.getFacturasNaoPagas().then(setFacturasNaoPagas).catch(() => {});
  }, []);

  useEffect(() => {
    paymentsService.getFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const servicoOptions = React.useMemo(
    () => filterOptions.servicos.map((s) => ({ value: s.codigo, label: s.label })),
    [filterOptions.servicos],
  );

  const eventoOptions = React.useMemo(
    () => (filterOptions.eventos || []).map((e) => ({ value: e.id, label: e.label })),
    [filterOptions.eventos],
  );

  const columns: Column<PaymentData>[] = [
    {
      id: 'reference',
      label: t('payments.reference'),
      minWidth: 120,
    },
    {
      id: 'customerName',
      label: t('payments.client'),
      minWidth: 150,
      format: (value) => value || <span style={{ color: '#999' }}>-</span>,
    },
    {
      id: 'entityName',
      label: t('payments.entity'),
      minWidth: 150,
      format: (value, row: any) => (
        <>
          {value || <span style={{ color: '#999' }}>-</span>}
          {row?.faturaNaoPaga && (
            <Chip size="small" color="error" label="Factura não paga" sx={{ ml: 0.5 }}
              title="A SISP aprovou como compra: o cliente foi cobrado mas a factura não chegou à entidade" />
          )}
        </>
      ),
    },
    {
      id: 'eventName',
      label: 'Evento',
      minWidth: 140,
      format: (value) => value || <span style={{ color: '#999' }}>-</span>,
    },
    {
      id: 'amount',
      label: t('payments.amount'),
      minWidth: 100,
      align: 'right',
      format: (value) => <CurrencyCell value={value} />,
    },
    {
      id: 'paymentMethod',
      label: t('payments.method'),
      minWidth: 100,
    },
    {
      id: 'createdAt',
      label: t('payments.date'),
      minWidth: 120,
      format: (value) => <DateCell value={value} includeTime />,
    },
    {
      id: 'status',
      label: t('payments.status'),
      minWidth: 100,
      align: 'center',
      format: (value) => {
        const isPaid = ['N', 'PAID', 'PG'].includes(value);
        const isFailed = ['F', 'FAILED', 'FL'].includes(value);
        const isCancelled = ['CANCELLED', 'CN'].includes(value);
        const isRefunded = ['REFUNDED', 'E'].includes(value);
        const label = isPaid ? 'Pago' : isFailed ? 'Falhou' : isCancelled ? 'Cancelado' : isRefunded ? 'Estornado' : 'Pendente';
        const color = isPaid ? 'success' : isFailed ? 'error' : isCancelled ? 'default' : isRefunded ? 'secondary' : 'warning';
        return <Chip size="small" label={label} color={color as any} />;
      },
    },
    {
      id: 'origin' as any,
      label: 'Origem',
      minWidth: 130,
      align: 'center',
      format: (_v: any, row?: any) => {
        const { label, color } = getOriginInfo(row?.origin, row?.source, row?.referer, row?.platform);
        return <Chip size="small" variant="outlined" color={color as any} label={label} />;
      },
    },
    {
      id: 'deviceType' as any,
      label: 'Device',
      minWidth: 70,
      align: 'center',
      format: (_v: any, row?: any) => {
        const dt = (row?.deviceType || '').toLowerCase();
        const plat = (row?.platform || '').toLowerCase();
        // «whatsapp» não é nem mobile nem desktop: sem este caso a coluna ficava
        // com um traço nos pagamentos feitos pela conversa.
        if (dt === 'whatsapp' || plat === 'whatsapp') return <Chip size="small" variant="outlined" label="WhatsApp" color="success" />;
        const isMobile = dt === 'mobile' || plat === 'mobile';
        const isDesktop = dt === 'desktop' || (!isMobile && plat === 'web');
        if (isMobile) return <Chip size="small" variant="outlined" label="Mobile" color="secondary" />;
        if (isDesktop) return <Chip size="small" variant="outlined" label="Desktop" color="default" />;
        return <span style={{ color: '#ccc' }}>-</span>;
      },
    },
  ];

  const handleView = (payment: PaymentData) => {
    setSelectedPayment(payment);
    setViewDialog(true);
  };

  const handleRefundConfirm = async () => {
    if (!refundDialog.row) return;
    setRefundLoading(true);
    setRefundError(null);
    try {
      const response = await sispService.requestRefund(refundDialog.row.id, refundMotivo);
      if (response.success) {
        setRefundDialog({ open: false, row: null });
        setRefundMotivo('');
        fetchPayments();
        paymentsService.getFacturasNaoPagas().then(setFacturasNaoPagas).catch(() => {});
      } else {
        setRefundError(response.message || 'Erro ao processar estorno');
      }
    } catch (err: any) {
      // O motivo real vem no corpo (ex.: «a compra foi feita no POS …»), não no texto do axios
      setRefundError(err?.response?.data?.error || err.message || 'Erro ao processar estorno');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleReconcileConfirm = async () => {
    setReconcileLoading(true);
    setReconcileResult(null);
    try {
      const res = await sispService.forceReconcile(reconcileDialog.merchantRef, true);
      setReconcileResult({ success: res.success, message: res.message });
      if (res.success) fetchPayments();
    } catch (err: any) {
      setReconcileResult({ success: false, message: err.response?.data?.error || err.message });
    } finally {
      setReconcileLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  if (loading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Facturas cobradas mas não pagas à entidade */}
      {facturasNaoPagas.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{facturasNaoPagas.length} pagamento{facturasNaoPagas.length > 1 ? 's' : ''} com a factura por pagar.</strong>{' '}
          A SISP aprovou-{facturasNaoPagas.length > 1 ? 'os' : 'o'} como compra: o cliente foi cobrado, mas a factura não chegou à entidade.
          Paga-se a factura pela Pagali ou devolve-se o valor ao cliente.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {facturasNaoPagas.slice(0, 10).map((f) => (
              <li key={f.reference}>
                {new Date(f.createdAt).toLocaleDateString('pt-PT')} · {f.customerName || '—'} · factura {f.invoiceReference} ·{' '}
                {Number(f.amount).toLocaleString('pt-PT')}$ · ref. SISP {f.reference}{' '}
                <Button size="small" color="inherit" sx={{ ml: 1, textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 0 }}
                  onClick={() => {
                    setRefundError(null);
                    setRefundMotivo(`Factura ${f.invoiceReference} cobrada como compra — não chegou à entidade`);
                    setRefundDialog({ open: true, row: { id: f.id, reference: f.reference, merchantRef: f.reference, amount: Number(f.amount), customerName: f.customerName, status: 'PAID', source: 'legacy' } as any });
                  }}>
                  Estornar
                </Button>
                <Button size="small" color="inherit" sx={{ ml: 1, textTransform: 'none', textDecoration: 'underline', p: 0, minWidth: 0 }}
                  onClick={async () => {
                    const nota = window.prompt(`Como foi resolvido o pagamento ${f.reference}?\n(ex.: «Factura paga pela Pagali a 24/10, ref. SISP …»)`);
                    if (!nota) return;
                    const r = await paymentsService.resolverFacturaNaoPaga(f.reference, nota);
                    if (!r.success) { window.alert(r.error || 'Erro ao registar'); return; }
                    setFacturasNaoPagas((lista) => lista.filter((x) => x.reference !== f.reference));
                    fetchPayments();
                  }}>
                  Marcar resolvida
                </Button>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('payments.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('payments.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>
            {t('common.export')}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchPayments}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <Payment fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{stats.pagos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('payments.paid')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'error.light', color: 'error.main' }}>
                  <Cancel fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{stats.falhados}</Typography>
                  <Typography variant="caption" color="text.secondary">Falhas reais</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  <AccessTime fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{stats.abandonados}</Typography>
                  <Typography variant="caption" color="text.secondary">Abandonados</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                  <AccessTime fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{stats.pendentes}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('payments.pending')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                  <TrendingUp fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(stats.valorTotal)}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('payments.totalValue')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label={t('payments.reference')}
              placeholder={t('payments.searchReference')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('payments.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="PAID">{t('payments.paid')}</MenuItem>
              <MenuItem value="PENDING">{t('payments.pending')}</MenuItem>
              <MenuItem value="FAILED">Falhados</MenuItem>
              <MenuItem value="CANCELLED">{t('payments.cancelled')}</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Origem"
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="sisp">SISP / Vinti4</MenuItem>
              <MenuItem value="legacy">Pagali</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FilterAutocomplete label="Entidade" value={entidadeFilter} options={filterOptions.entidades}
              onChange={(v) => { setEntidadeFilter(v); setPage(0); }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FilterAutocomplete label="Serviço" value={servicoFilter} placeholderTodos="Todos"
              options={servicoOptions} onChange={(v) => { setServicoFilter(v); setPage(0); }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FilterAutocomplete label="Evento" value={eventoFilter} placeholderTodos="Todos"
              options={eventoOptions} onChange={(v) => { setEventoFilter(v); setPage(0); }} />
          </Grid>
        </Grid>
      </Paper>

      {/* Status Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label={`Pagos (${stats.pagos})`} icon={<CheckCircle fontSize="small" color="success" />} iconPosition="start" />
          <Tab label="Pendentes" icon={<AccessTime fontSize="small" color="warning" />} iconPosition="start" />
          <Tab label="Falhados" icon={<Cancel fontSize="small" color="error" />} iconPosition="start" />
          <Tab label="Cancelados" />
          <Tab label={`Todos (${stats.total})`} />
        </Tabs>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={payments}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchPayments}
        onView={handleView}
        extraActions={(row) => {
          const isPaidRow = ['N', 'PAID', 'PG', 'PAGO'].includes(row.status);
          // Página SISP (pg_pagamentos_sisp) e pagamentos antigos/cartão guardado
          // (pg_sisp_pedido) — a API estorna os dois, pelo POS em que a compra foi feita.
          const source = (row as any).source;
          if (!['sisp', 'legacy'].includes(source) || !isPaidRow) return null;
          return (
            <Tooltip title="Estornar pagamento">
              <IconButton size="small" color="warning" onClick={() => { setRefundError(null); setRefundMotivo(''); setRefundDialog({ open: true, row }); }}>
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }}
        rowKey="id"
        emptyMessage="Nenhum pagamento encontrado"
      />

      {/* Dialog de estorno */}
      <Dialog open={refundDialog.open} onClose={() => !refundLoading && setRefundDialog({ open: false, row: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Undo /> Solicitar Estorno
        </DialogTitle>
        <DialogContent dividers>
          {refundError && <Alert severity="error" sx={{ mb: 2 }}>{refundError}</Alert>}
          {refundDialog.row && (
            <Stack spacing={2}>
              <Alert severity="warning">Tem a certeza que deseja estornar este pagamento?</Alert>
              <Table size="small">
                <TableBody>
                  <TableRow><TableCell sx={{ fontWeight: 600, width: 130 }}>Referência</TableCell><TableCell><code>{refundDialog.row.reference}</code></TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell><TableCell>{refundDialog.row.customerName || '-'}</TableCell></TableRow>
                  <TableRow><TableCell sx={{ fontWeight: 600 }}>Valor</TableCell><TableCell><strong>{formatCurrency(Number(refundDialog.row.amount))}</strong></TableCell></TableRow>
                </TableBody>
              </Table>
              <TextField
                fullWidth multiline rows={3}
                label="Motivo do Estorno"
                placeholder="Descreva o motivo do estorno..."
                value={refundMotivo}
                onChange={(e) => setRefundMotivo(e.target.value)}
                disabled={refundLoading}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog({ open: false, row: null })} disabled={refundLoading}>Cancelar</Button>
          <Button
            variant="contained" color="error"
            startIcon={refundLoading ? <CircularProgress size={18} color="inherit" /> : <Undo />}
            onClick={handleRefundConfirm}
            disabled={refundLoading}
          >
            Confirmar Estorno
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Detail Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detalhes do Pagamento
          <IconButton size="small" onClick={() => setViewDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (() => {
            const isPaid = ['N', 'PAID', 'PG'].includes(selectedPayment.status);
            const isFailed = ['F', 'FAILED', 'FL'].includes(selectedPayment.status);
            const isRefunded = ['REFUNDED', 'E'].includes(selectedPayment.status);
            const statusLabel = isPaid ? 'Pago' : isFailed ? 'Falhou' : isRefunded ? 'Estornado' : ['CANCELLED', 'CN'].includes(selectedPayment.status) ? 'Cancelado' : 'Pendente';
            const statusColor = isPaid ? 'success' : isFailed ? 'error' : isRefunded ? 'secondary' : 'warning';
            return (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {formatCurrency(Number(selectedPayment.amount) || 0)}
                  </Typography>
                  <Chip label={statusLabel} color={statusColor as any} sx={{ mt: 1 }} />
                </Box>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 150 }}>Referência</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <code>{selectedPayment.reference}</code>
                          <IconButton size="small" onClick={() => navigator.clipboard.writeText(selectedPayment.reference)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {selectedPayment.invoiceReference && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Ref. Interna</TableCell>
                        <TableCell><code>{selectedPayment.invoiceReference}</code></TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                      <TableCell>{selectedPayment.customerName || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Entidade</TableCell>
                      <TableCell>{selectedPayment.entityName || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Método</TableCell>
                      <TableCell>{selectedPayment.paymentMethod || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                      <TableCell>{selectedPayment.createdAt ? new Date(selectedPayment.createdAt).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Origem</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={(selectedPayment as any).source === 'sisp' ? 'primary' : 'default'}
                          label={(selectedPayment as any).source === 'sisp' ? 'SISP / Vinti4' : 'Pagali'}
                        />
                      </TableCell>
                    </TableRow>
                    {(selectedPayment as any).merchantRef && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Ref. SISP</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{(selectedPayment as any).merchantRef}</TableCell>
                    </TableRow>
                    )}
                    {(selectedPayment as any).tokenRecarga && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Token Recarga</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main', fontSize: '1.1rem' }}>{(selectedPayment as any).tokenRecarga}</TableCell>
                    </TableRow>
                    )}
                    {(selectedPayment as any).customerEmail && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell>{(selectedPayment as any).customerEmail}</TableCell>
                    </TableRow>
                    )}
                    {(selectedPayment as any).deviceType && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Dispositivo</TableCell>
                      <TableCell><Chip size="small" label={(selectedPayment as any).deviceType === 'whatsapp' ? 'WhatsApp' : (selectedPayment as any).deviceType === 'mobile' ? 'Mobile' : 'Desktop'} /></TableCell>
                    </TableRow>
                    )}
                    {(selectedPayment as any).referer && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Site Origem</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{(selectedPayment as any).referer}</TableCell>
                    </TableRow>
                    )}
                    {(selectedPayment as any).ipAddress && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{(selectedPayment as any).ipAddress}</TableCell>
                    </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Motivo do erro / resposta SISP */}
                {(() => {
                  const p = selectedPayment as any;
                  const isFailed = ['F', 'FAILED', 'FL'].includes(p.status);
                  const isPending = !['N', 'PAID', 'PG', 'F', 'FAILED', 'FL', 'CANCELLED', 'CN'].includes(p.status);
                  const hasError = p.sispErrorCode || p.sispErrorDescription;

                  if (p.source !== 'sisp') return null;

                  // Só é «motivo da falha» se o pagamento FALHOU: as colunas de
                  // resposta SISP guardam também o sucesso (código 00, tipo 8),
                  // e num pagamento Pago a caixa vermelha rotulava-o de falha.
                  if (isFailed && hasError) {
                    return (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>
                          ⚠️ Motivo da falha (SISP)
                        </Typography>
                        {p.sispErrorCode && (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                            Código: {p.sispErrorCode} {p.sispMessageType ? `(tipo ${p.sispMessageType})` : ''}
                          </Typography>
                        )}
                        {p.sispErrorDescription && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>{p.sispErrorDescription}</Typography>
                        )}
                        {p.sispErrorDetail && p.sispErrorDetail !== p.sispErrorDescription && (
                          <Typography variant="body2" color="text.secondary">{p.sispErrorDetail}</Typography>
                        )}
                        {p.sispAdditionalError && p.sispAdditionalError.trim() && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {p.sispAdditionalError.trim()}
                          </Typography>
                        )}
                      </Box>
                    );
                  }

                  if (isPending) {
                    return (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 0.5 }}>
                          ⏳ Sem resposta do SISP
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          O pagamento foi iniciado mas não chegou ao SISP — o cliente pode ter abandonado a página antes de confirmar.
                        </Typography>
                      </Box>
                    );
                  }

                  return null;
                })()}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          {/* Reconciliação manual — só para SISP cancelado/expirado com merchantRef */}
          {selectedPayment && (selectedPayment as any).source === 'sisp' &&
           (selectedPayment as any).merchantRef &&
           ['CANCELLED', 'EXPIRED', 'CN', 'EX'].includes(selectedPayment.status) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<SyncAlt />}
              onClick={() => {
                setReconcileResult(null);
                setReconcileDialog({ open: true, merchantRef: (selectedPayment as any).merchantRef });
              }}
            >
              Reconciliar Manualmente
            </Button>
          )}
          <Button onClick={() => setViewDialog(false)} sx={{ ml: 'auto' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de reconciliação manual */}
      <Dialog open={reconcileDialog.open} onClose={() => !reconcileLoading && setReconcileDialog({ open: false, merchantRef: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncAlt /> Reconciliação Manual SISP
        </DialogTitle>
        <DialogContent dividers>
          {reconcileResult ? (
            <Alert severity={reconcileResult.success ? 'success' : 'error'}>
              {reconcileResult.message}
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Alert severity="warning">
                Use esta opção <strong>apenas quando o portal SISP confirma o pagamento</strong> mas o pedido ficou cancelado no nosso sistema (callback perdido).
              </Alert>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 130 }}>Ref. SISP</TableCell>
                    <TableCell><code>{reconcileDialog.merchantRef}</code></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="body2" color="text.secondary">
                Esta ação irá: confirmar o pedido, activar os bilhetes e reenviar o email ao comprador.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialog({ open: false, merchantRef: '' })} disabled={reconcileLoading}>
            {reconcileResult?.success ? 'Fechar' : 'Cancelar'}
          </Button>
          {!reconcileResult && (
            <Button
              variant="contained"
              color="warning"
              startIcon={reconcileLoading ? <CircularProgress size={18} color="inherit" /> : <SyncAlt />}
              onClick={handleReconcileConfirm}
              disabled={reconcileLoading}
            >
              Confirmar Reconciliação
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
